import { Button, Text } from "@radix-ui/themes";
import { useNavigate, useParams } from "react-router-dom";
import { useCheckoutQuery, useMessageQuery } from "../../api/hitl-api";
import { useEffect, useState } from "react";
import { Instructions } from "../../components/instructions";
import { Sidebar } from "../../components/sidebar";
import { ProductsTable } from "../../components/products-table";
import { CheckoutStatus, MessageCodes } from "../../components/checkout-status";
import { setMessageContent } from "../../store/slices/modalsSlice";
import { useAppDispatch, useAppSelector } from "../../store";
import { PaymentModal } from "../../components/payment/payment-modal";
import { ErrorInstructionsModal } from "../../components/error-instructions";
import { useTranslation } from "react-i18next";
import { getMainUrl } from "../../shared/consts";
import { SkuInBoxPaymentProvider } from "../../components/payment/payment-context";
import { useCheckout } from "../../components/checkout/checkout-context";
import { InactivityAlertModal } from "../../components/inactivity-alert";
import { AxiosError } from "axios";

export function Checkout() {
  const { key = "", zoneId = "", storeKey = "" } = useParams();
  const { t } = useTranslation();
  const [showInstructions, toggleInstructions] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const messageContent = useAppSelector((state) => state.modals.messageContent);
  const dispatch = useAppDispatch();
  const [autoCompleteEnabled, setAutoComplete] = useState(true);
  const { cancelSession, isCancelLoading } = useCheckout();

  const checkoutQuery = useCheckoutQuery(key, {
    pollingInterval: pollingEnabled ? 500 : 0,
  });
  const { data: message } = useMessageQuery(key, {
    pollingInterval: pollingEnabled ? 500 : 0,
  });

  const data = checkoutQuery.data;
  const navigate = useNavigate();

  useEffect(() => {
    if (checkoutQuery.isError && (checkoutQuery.error as AxiosError)?.status === 400) {
      navigate(getMainUrl({ zoneId, storeKey, mode: "sku-in-box" }));
    }
  }, [checkoutQuery.isError]);

  useEffect(() => {
    if (data && data.result && data.result.lines.length > 0) {
      toggleInstructions(false);
    } else if (data && data.result && data.result.lines.length === 0) {
      toggleInstructions(true);
    }
  }, [data]);

  useEffect(() => {
    if (message && message.result) {
      const isEvaluationCompleted = message.result.message_codes[0] === MessageCodes.EvaluationCompleted;
      const isManualModeStarted = message.result.message_codes.includes(MessageCodes.ManualModeStarted);

      if (isEvaluationCompleted) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }

      if (isManualModeStarted) {
        setAutoComplete(false);
      }

      // If message is evaluation_completed and cart is empty, do not display error instruction
      if (isEvaluationCompleted && data?.result.lines.length === 0) {
        return;
      }

      const onlyErrorCodes = message.result.message_codes.filter(
        (code) =>
          ![MessageCodes.EvaluationCompleted, MessageCodes.ManualModeStarted].includes(code as MessageCodes)
      );
      const messageContent = {
        ...message.result,
        message_codes: onlyErrorCodes,
      };

      dispatch(setMessageContent(messageContent));
    }
  }, [message]);

  useEffect(() => {
    return () => {
      dispatch(setMessageContent(null));
    };
  }, []);

  const isErrorDialogOpen =
    !!messageContent &&
    messageContent.message_codes.length > 0 &&
    messageContent.message_codes[0] !== MessageCodes.EvaluationCompleted;

  return (
    <div className="max-h-screen h-screen w-full bg-tezzro-bg">
      {showInstructions && !data?.result?.lines.length && (
        <Instructions messageContent={messageContent} loading={isCancelLoading} onCancel={cancelSession} />
      )}
      {data && data.result && data.result.lines.length > 0 && (
        <SkuInBoxPaymentProvider lines={data.result.lines}>
          <div className="flex flex-col h-full p-8 gap-3">
            <header className="flex justify-between p-4 bg-white border border-card-border rounded-2xl">
              <Text size="8" weight="medium" color="gray" highContrast>
                {t("cardDetails")}
              </Text>
              <Button color="red" size="3" variant="soft" disabled={isCancelLoading} onClick={cancelSession}>
                {t("cancelCheckout")}
              </Button>
            </header>
            <div className="flex flex-grow gap-3">
              <main className="relative flex-grow rounded-2xl bg-white border border-card-border">
                <CheckoutStatus isCompleted={isCompleted} />
                <ProductsTable
                  lines={data.result?.lines}
                  enableTimer={autoCompleteEnabled}
                  onComplete={() => setIsCompleted(true)}
                />
              </main>
              <Sidebar isCompleted={isCompleted} data={data.result} />
            </div>
            <PaymentModal totalAmount={data.result.price_with_tax} setPolling={setPollingEnabled} />
            <ErrorInstructionsModal
              open={isErrorDialogOpen}
              messageContent={messageContent}
              onCancel={cancelSession}
            />
            {isCompleted && <InactivityAlertModal />}
          </div>
        </SkuInBoxPaymentProvider>
      )}
    </div>
  );
}
